import type { RunSettings } from '../multiplayer/RunSettings';
import Phaser from 'phaser';
import { GAME_W, GAME_H } from '../constants';
import { GameMode } from '../types';
import { saveSystem } from '../systems/SaveSystem';
import { audioSystem } from '../systems/AudioSystem';

// ── Types ────────────────────────────────────────────────────────────────────

export interface GameOverData extends RunSettings {
  score: number;
  level: number;
  mode: GameMode;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  alpha: number;
  color: number;
  size: number;
  rotation: number;
  rotSpeed: number;
}

// ── Scene ────────────────────────────────────────────────────────────────────

export default class GameOverScene extends Phaser.Scene {
  private sceneData!: GameOverData;
  private isNewHighScore = false;
  private particles: Particle[] = [];
  private particleGraphics!: Phaser.GameObjects.Graphics;

  constructor() {
    super({ key: 'GameOverScene' });
  }

  init(data: GameOverData): void {
    this.sceneData = {
      playerCount: data?.playerCount, playerNames: data?.playerNames, testStage: data?.testStage,
      score: data?.score ?? 0,
      level: data?.level ?? 1,
      mode: data?.mode ?? GameMode.CAMPAIGN,
    };
    // Check / save high score
    this.isNewHighScore = this.sceneData.score > saveSystem.highScore;
    if (this.isNewHighScore) {
      saveSystem.updateHighScore(this.sceneData.score);
    }
    saveSystem.incrementStat('totalGames');
  }

  create(): void {
    audioSystem.play('game_over');

    this.buildBackground();
    this.buildTitle();
    this.buildStats();
    this.buildButtons();
    this.buildParticles();

    // Fade in
    this.cameras.main.setAlpha(0);
    this.tweens.add({ targets: this.cameras.main, alpha: 1, duration: 400, ease: 'Linear' });
  }

  update(): void {
    this.updateParticles();
  }

  // ── Background ────────────────────────────────────────────────────────────

  private buildBackground(): void {
    // Full dark overlay
    this.add.rectangle(GAME_W / 2, GAME_H / 2, GAME_W, GAME_H, 0x000000, 0.88).setDepth(0);

    // Blood-red vignette glow
    const glow = this.add.graphics().setDepth(1);
    glow.fillStyle(0x330000, 0.4);
    glow.fillCircle(GAME_W / 2, GAME_H / 2 - 60, 420);

    // Subtle red grid
    const grid = this.add.graphics().setDepth(1).setAlpha(0.08);
    grid.lineStyle(1, 0xff0000, 1);
    for (let x = 0; x < GAME_W; x += 48) grid.lineBetween(x, 0, x, GAME_H);
    for (let y = 0; y < GAME_H; y += 48) grid.lineBetween(0, y, GAME_W, y);
  }

  // ── Title ─────────────────────────────────────────────────────────────────

  private buildTitle(): void {
    const cx = GAME_W / 2;

    // Shadow layer
    this.add.text(cx + 4, 104, 'GAME OVER', {
      fontFamily: 'Arial Black, Arial',
      fontSize: '88px',
      color: '#110000',
    }).setOrigin(0.5).setDepth(3);

    // Main red text
    const titleText = this.add.text(cx, 100, 'GAME OVER', {
      fontFamily: 'Arial Black, Arial',
      fontSize: '88px',
      color: '#ff1111',
      stroke: '#660000',
      strokeThickness: 8,
      shadow: { offsetX: 0, offsetY: 0, color: '#ff0000', blur: 24, fill: true },
    }).setOrigin(0.5).setDepth(4);

    // Flicker animation
    this.buildFlicker(titleText);

    // Decorative lines
    const dec = this.add.graphics().setDepth(4);
    dec.lineStyle(3, 0xff1111, 0.7);
    dec.lineBetween(cx - 260, 148, cx + 260, 148);
    dec.lineStyle(1, 0xff4444, 0.4);
    dec.lineBetween(cx - 220, 156, cx + 220, 156);
  }

  private buildFlicker(obj: Phaser.GameObjects.Text): void {
    // Flicker: rapid random alpha changes mimicking a dying neon sign
    const flick = () => {
      const delay = Phaser.Math.Between(80, 600);
      this.time.delayedCall(delay, () => {
        const isOn = Math.random() > 0.25;
        this.tweens.add({
          targets: obj,
          alpha: isOn ? 1 : Phaser.Math.FloatBetween(0.2, 0.7),
          duration: 40,
          onComplete: () => flick(),
        });
      });
    };
    flick();
  }

  // ── Stats Panel ───────────────────────────────────────────────────────────

  private buildStats(): void {
    const cx = GAME_W / 2;
    const panelW = 420;
    const panelH = 240;
    const panelX = cx - panelW / 2;
    const panelY = 175;

    // Panel background
    const panel = this.add.graphics().setDepth(4);
    panel.fillStyle(0x0a0a1a, 0.92);
    panel.fillRoundedRect(panelX, panelY, panelW, panelH, 14);
    panel.lineStyle(2, 0x440000, 0.8);
    panel.strokeRoundedRect(panelX, panelY, panelW, panelH, 14);
    panel.lineStyle(2, 0xff1111, 0.4);
    panel.strokeRoundedRect(panelX + 4, panelY + 4, panelW - 8, panelH - 8, 11);

    // Mode label
    const modeLabel = this.getModeLabel(this.sceneData.mode);
    this.add.text(cx, panelY + 22, modeLabel, {
      fontFamily: 'Arial',
      fontSize: '12px',
      color: '#664444',
      letterSpacing: 3,
    }).setOrigin(0.5).setDepth(5);

    // Score
    this.add.text(cx, panelY + 50, 'SCOR FINAL', {
      fontFamily: 'Arial',
      fontSize: '13px',
      color: '#886666',
      letterSpacing: 2,
    }).setOrigin(0.5).setDepth(5);

    this.add.text(cx, panelY + 80, this.sceneData.score.toLocaleString(), {
      fontFamily: 'Arial Black, Arial',
      fontSize: '48px',
      color: '#ffaa00',
      stroke: '#553300',
      strokeThickness: 4,
    }).setOrigin(0.5).setDepth(5);

    // Level reached
    this.add.text(cx - 80, panelY + 138, 'NIVEL ATINS', {
      fontFamily: 'Arial',
      fontSize: '12px',
      color: '#664444',
      letterSpacing: 2,
    }).setOrigin(0.5).setDepth(5);

    this.add.text(cx - 80, panelY + 160, `${this.sceneData.level}`, {
      fontFamily: 'Arial Black, Arial',
      fontSize: '28px',
      color: '#ff8888',
    }).setOrigin(0.5).setDepth(5);

    // High score section
    const hs = saveSystem.highScore;
    this.add.text(cx + 80, panelY + 138, 'SCOR MAXIM', {
      fontFamily: 'Arial',
      fontSize: '12px',
      color: '#664444',
      letterSpacing: 2,
    }).setOrigin(0.5).setDepth(5);

    this.add.text(cx + 80, panelY + 160, hs.toLocaleString(), {
      fontFamily: 'Arial Black, Arial',
      fontSize: '28px',
      color: this.isNewHighScore ? '#ffff00' : '#ff8888',
    }).setOrigin(0.5).setDepth(5);

    // New high score badge
    if (this.isNewHighScore) {
      const badge = this.add.text(cx, panelY + 196, '★  NOU RECORD PERSONAL!  ★', {
        fontFamily: 'Arial Black, Arial',
        fontSize: '15px',
        color: '#ffff00',
        stroke: '#554400',
        strokeThickness: 3,
        shadow: { offsetX: 0, offsetY: 0, color: '#ffff00', blur: 12, fill: true },
      }).setOrigin(0.5).setDepth(5);

      this.tweens.add({
        targets: badge,
        scaleX: 1.08, scaleY: 1.08,
        duration: 600,
        yoyo: true, repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  private getModeLabel(mode: GameMode): string {
    switch (mode) {
      case GameMode.CAMPAIGN: return 'MOD: CAMPANIE';
      case GameMode.ARCADE: return 'MOD: ARCADE CLASIC';
      case GameMode.SURVIVAL: return 'MOD: SUPRAVIEȚUIRE';
      case GameMode.MULTIPLAYER: return 'MOD: MULTIPLAYER';
      default: return 'MOD: NECUNOSCUT';
    }
  }

  // ── Buttons ───────────────────────────────────────────────────────────────

  private buildButtons(): void {
    const cx = GAME_W / 2;
    const baseY = 460;
    const btnW = 280;
    const btnH = 52;

    const btnData: Array<{ label: string; color: number; x: number; action: () => void }> = [
      {
        label: '↺  ÎNCEARCĂ DIN NOU',
        color: 0x00ff88,
        x: cx - 155,
        action: () => this.retryGame(),
      },
      {
        label: '⌂  MENIU PRINCIPAL',
        color: 0xff4444,
        x: cx + 155,
        action: () => this.goToMainMenu(),
      },
    ];

    btnData.forEach((bd, i) => {
      const container = this.add.container(bd.x, baseY).setDepth(10);

      const bg = this.add.graphics();
      bg.fillStyle(0x0a0a22, 0.95);
      bg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 10);
      bg.lineStyle(2, bd.color, 0.6);
      bg.strokeRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 10);
      container.add(bg);

      const label = this.add.text(0, 1, bd.label, {
        fontFamily: 'Arial Black, Arial',
        fontSize: '17px',
        color: Phaser.Display.Color.IntegerToColor(bd.color).rgba,
        stroke: '#000000',
        strokeThickness: 3,
      }).setOrigin(0.5);
      container.add(label);

      const hit = this.add.rectangle(0, 0, btnW, btnH, 0xffffff, 0);
      hit.setInteractive({ useHandCursor: true });
      container.add(hit);

      const glow = this.add.graphics();
      glow.fillStyle(bd.color, 0.12);
      glow.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 10);
      glow.setAlpha(0);
      container.add(glow);

      hit.on('pointerover', () => {
        audioSystem.play('menu_hover');
        glow.setAlpha(1);
        this.tweens.add({ targets: container, scaleX: 1.05, scaleY: 1.05, duration: 80, ease: 'Linear' });
      });
      hit.on('pointerout', () => {
        glow.setAlpha(0);
        this.tweens.add({ targets: container, scaleX: 1, scaleY: 1, duration: 80, ease: 'Linear' });
      });
      hit.on('pointerdown', () => {
        audioSystem.play('menu_click');
        this.tweens.add({
          targets: container,
          scaleX: 0.94, scaleY: 0.94,
          duration: 60, yoyo: true, ease: 'Linear',
          onComplete: () => bd.action(),
        });
      });

      // Entrance
      container.setAlpha(0).setY(baseY + 40);
      this.tweens.add({
        targets: container,
        y: baseY, alpha: 1,
        duration: 300, delay: 400 + i * 80,
        ease: 'Back.easeOut',
      });
    });

    // Keyboard shortcuts hint
    this.add.text(cx, baseY + 50, 'R — Încearcă din nou   |   ESC — Meniu principal', {
      fontFamily: 'Arial',
      fontSize: '12px',
      color: '#443333',
    }).setOrigin(0.5).setDepth(8);

    this.input.keyboard!.on('keydown-R', () => this.retryGame());
    this.input.keyboard!.on('keydown-ESC', () => this.goToMainMenu());
    this.input.keyboard!.on('keydown-ENTER', () => this.retryGame());
  }

  // ── Particles (debris/ember effect) ───────────────────────────────────────

  private buildParticles(): void {
    this.particleGraphics = this.add.graphics().setDepth(2);

    const emberColors = [0xff2200, 0xff5500, 0xff8800, 0xffaa00, 0xffffff];

    for (let i = 0; i < 40; i++) {
      this.particles.push({
        x: Phaser.Math.Between(0, GAME_W),
        y: GAME_H + Phaser.Math.Between(0, 100),
        vx: Phaser.Math.FloatBetween(-25, 25),
        vy: Phaser.Math.FloatBetween(-60, -20),
        alpha: Phaser.Math.FloatBetween(0.4, 1),
        color: Phaser.Utils.Array.GetRandom(emberColors) as number,
        size: Phaser.Math.Between(2, 5),
        rotation: Phaser.Math.FloatBetween(0, Math.PI * 2),
        rotSpeed: Phaser.Math.FloatBetween(-2, 2),
      });
    }
  }

  private updateParticles(): void {
    const dt = this.game.loop.delta / 1000;
    this.particleGraphics.clear();

    for (const p of this.particles) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += 18 * dt; // gravity pulls embers back
      p.rotation += p.rotSpeed * dt;
      p.alpha -= 0.004;

      if (p.alpha <= 0 || p.y > GAME_H + 20) {
        // Respawn from bottom
        p.x = Phaser.Math.Between(0, GAME_W);
        p.y = GAME_H + 10;
        p.vx = Phaser.Math.FloatBetween(-25, 25);
        p.vy = Phaser.Math.FloatBetween(-60, -20);
        p.alpha = Phaser.Math.FloatBetween(0.5, 1);
        p.size = Phaser.Math.Between(2, 5);
      }

      this.particleGraphics.fillStyle(p.color, p.alpha);
      this.particleGraphics.fillRect(
        p.x - p.size / 2,
        p.y - p.size / 2,
        p.size,
        p.size
      );
    }
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  private retryGame(): void {
    this.cameras.main.fadeOut(280, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('GameScene', {
        playerCount: this.sceneData.playerCount, playerNames: this.sceneData.playerNames, testStage: this.sceneData.testStage,
        level: 1,
        mode: this.sceneData.mode,
        score: 0,
      });
    });
  }

  private goToMainMenu(): void {
    audioSystem.stopMusic();
    this.cameras.main.fadeOut(280, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('MainMenuScene');
    });
  }
}
